import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { ArrowLeft, Blocks, Trophy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const BlockchainBuilder = () => {
  const navigate = useNavigate();
  const { fetchProfile } = useAuth();
  const [gameStarted, setGameStarted] = useState(false);
  const [chain, setChain] = useState([]);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);

  const blocks = [
    { id: 1, data: 'Genesis Block', prevHash: '0000', hash: 'a1b2' },
    { id: 2, data: 'Transaction 1', prevHash: 'a1b2', hash: 'c3d4' },
    { id: 3, data: 'Transaction 2', prevHash: 'c3d4', hash: 'e5f6' },
    { id: 4, data: 'Transaction 3', prevHash: 'e5f6', hash: 'g7h8' },
  ];

  const [availableBlocks, setAvailableBlocks] = useState(blocks.sort(() => Math.random() - 0.5));

  const addBlock = (block) => {
    if (chain.length === 0 && block.prevHash === '0000') {
      setChain([block]);
      setAvailableBlocks(availableBlocks.filter(b => b.id !== block.id));
      setScore(score + 250);
      toast.success('Correct! +250 points');
    } else if (chain.length > 0 && block.prevHash === chain[chain.length - 1].hash) {
      setChain([...chain, block]);
      setAvailableBlocks(availableBlocks.filter(b => b.id !== block.id));
      setScore(score + 250);
      toast.success('Correct! +250 points');
      
      if (chain.length === blocks.length - 1) {
        setTimeout(() => endGame(), 1500);
      }
    } else {
      toast.error('Wrong block! Check the previous hash.');
    }
  };

  const endGame = async () => {
    setGameOver(true);
    try {
      const response = await axios.post(`${API}/games/submit`, {
        game_type: 'builder',
        score: score,
        time_taken: 0
      });
      toast.success(`Blockchain built! Earned ${response.data.xp_gained} XP`);
      await fetchProfile();
    } catch (error) {
      console.error('Failed to submit score', error);
    }
  };

  if (!gameStarted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="max-w-md w-full mx-auto px-6">
          <div className="glass-panel rounded-xl p-8 text-center" data-testid="game-start-screen">
            <Blocks className="w-16 h-16 text-primary mx-auto mb-4" />
            <h1 className="text-3xl font-grotesk font-bold text-white mb-4">Blockchain Builder</h1>
            <p className="text-zinc-400 mb-6 leading-relaxed">
              Build a valid blockchain by connecting blocks in the correct order. Match previous hashes with block hashes to create the chain!
            </p>
            <Button
              onClick={() => setGameStarted(true)}
              data-testid="start-game-button"
              className="bg-primary text-black font-bold hover:bg-primary/90 hover:shadow-green-glow uppercase tracking-wide px-8 py-6 text-base h-auto"
            >
              Start Building
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/games')}
              className="mt-4 text-zinc-400 hover:text-primary"
            >
              Back to Games
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (gameOver) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="max-w-md w-full mx-auto px-6">
          <div className="glass-panel rounded-xl p-8 text-center" data-testid="game-over-screen">
            <Trophy className="w-16 h-16 text-primary mx-auto mb-4" />
            <h1 className="text-3xl font-grotesk font-bold text-white mb-4">Blockchain Complete!</h1>
            <div className="mb-6">
              <div className="text-5xl font-mono font-bold text-primary mb-2" data-testid="final-score">{score}</div>
              <div className="text-zinc-400">Total Score</div>
            </div>
            <Button
              onClick={() => window.location.reload()}
              data-testid="play-again-button"
              className="bg-primary text-black font-bold hover:bg-primary/90 uppercase tracking-wide px-8 py-3 mb-3 w-full"
            >
              Build Again
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/games')}
              className="text-zinc-400 hover:text-primary w-full"
            >
              Back to Games
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto px-6 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/games')}
          data-testid="back-to-games-button"
          className="mb-4 text-zinc-400 hover:text-primary"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Exit Builder
        </Button>

        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-grotesk font-bold text-white">Blockchain Builder</h1>
          <div className="text-2xl font-mono font-bold text-primary" data-testid="game-score">{score}</div>
        </div>

        <div className="mb-8">
          <h3 className="text-sm font-mono uppercase tracking-wider text-primary/80 mb-4">Your Blockchain</h3>
          <div className="space-y-3">
            {chain.length === 0 ? (
              <div className="bg-zinc-900/30 border border-white/10 rounded-xl p-6 text-center text-zinc-500">
                Start by adding the Genesis Block
              </div>
            ) : (
              chain.map((block, index) => (
                <div key={block.id} className="bg-primary/10 border-2 border-primary rounded-xl p-6" data-testid={`chain-block-${block.id}`}>
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-xs font-mono uppercase tracking-wider text-primary/80">Block {index}</span>
                    <span className="text-xs font-mono text-primary">#{block.id}</span>
                  </div>
                  <div className="text-white font-semibold mb-2">{block.data}</div>
                  <div className="text-xs font-mono text-zinc-400">
                    <div>Prev: {block.prevHash}</div>
                    <div>Hash: {block.hash}</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div>
          <h3 className="text-sm font-mono uppercase tracking-wider text-primary/80 mb-4">Available Blocks</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {availableBlocks.map((block) => (
              <button
                key={block.id}
                onClick={() => addBlock(block)}
                data-testid={`available-block-${block.id}`}
                className="bg-zinc-900/30 border border-white/10 rounded-xl p-6 text-left hover:border-primary/50 hover:bg-zinc-800/50"
              >
                <div className="flex justify-between items-start mb-2">
                  <span className="text-xs font-mono uppercase tracking-wider text-zinc-500">Block</span>
                  <span className="text-xs font-mono text-primary">#{block.id}</span>
                </div>
                <div className="text-white font-semibold mb-2">{block.data}</div>
                <div className="text-xs font-mono text-zinc-400">
                  <div>Prev: {block.prevHash}</div>
                  <div>Hash: {block.hash}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlockchainBuilder;
